import { request, type AuthContext } from './client'
import type { InputMode, RecordingSource } from '../types/prisma-enums'
import type { AnswerMovement, QuestionOutcome, QuestionRecord } from '../types/questionRecord'

/**
 * The per-question read API (`/learning/*`) — every question a child answered,
 * as the child, the parent, the teacher, the school admin and Kalqy staff see
 * it. Types and fetchers only; the endpoints are built against
 * `backend/src/modules/answers/answers.types.ts`, which holds the same shapes
 * (a mirror, never an import — the backend may not depend on the frontend).
 *
 * 🔴 PAGES ARE KEYSET, NEVER OFFSET: `nextCursor` is opaque, `limit ≤ 100`, and
 * the order is newest first on (createdAt, id). Pass `nextCursor` back as
 * `cursor` until it is null.
 *
 * 🔴 I1. Nothing here is body-derived: `movement` is the act the device decided
 * the child made, and the durations are between two decisions, not two frames.
 *
 * Scoping is the server's: a parent naming another family's child, or a teacher
 * another class, is a 403.
 */

export type { AnswerMovement, QuestionOutcome, QuestionRecord }

export type GameId = 'sky-quest' | 'letter-lagoon' | 'turos-world' | 'kalqy-world' | 'bridge-patterns'
export type LearningArea = 'numbers' | 'letters_words' | 'general_awareness' | 'social_awareness' | 'matching_sorting'

export const AREA_BY_GAME: Record<GameId, LearningArea> = {
  'sky-quest': 'numbers',
  'letter-lagoon': 'letters_words',
  'turos-world': 'general_awareness',
  'kalqy-world': 'social_awareness',
  'bridge-patterns': 'matching_sorting',
}

/** The largest page any `/learning` list returns. */
export const PAGE_LIMIT_MAX = 100

/** Keyset on (createdAt, id); limit ≤ 100; never OFFSET. */
export interface Page<T> {
  rows: T[]
  nextCursor: string | null
}

export interface AnswerRow {
  id: string
  /** ISO time it was played. */
  at: string
  sessionId: string | null
  game: GameId
  level: 1 | 2 | 3
  itemId: string
  outcome: QuestionOutcome
  attempt: number
  /** outcome = correct and attempt = 1. */
  firstTry: boolean
  given: string | null
  expected: string | null
  ttrMs: number | null
  ttfMs: number | null
  movement: AnswerMovement
  moved: boolean
  gesture: string | null
  inputMode: InputMode | null
}

/** `avgTtfMs` / `medianTtfMs` (hesitation) count FIRST attempts only, like every time figure here. */
export interface MovementSummary {
  byMovement: Record<AnswerMovement, number>
  itemsMoved: number
  itemsStill: number
  avgTtfMs: number | null
  medianTtfMs: number | null
  posesMet: number
  posesMissed: number
}

export interface AreaSummary {
  game: GameId
  area: LearningArea
  questions: number
  answers: number
  correct: number
  firstTry: number
  wrong: number
  noAnswer: number
  /**
   * 🔴 FIRST ATTEMPTS ONLY (attempt = 1 with a time). The games time a retry
   * differently, so a retry's time is not comparable across games; the counts
   * above still include every try.
   */
  avgTtrMs: number | null
  medianTtrMs: number | null
  levels: { level: 1 | 2 | 3; questions: number; correct: number; completed: boolean }[]
  movement: MovementSummary
  lastPlayedAt: string | null
}

export interface ActivityDay {
  /** `YYYY-MM-DD`, IST. */
  day: string
  sessions: number
  minutes: number
  games: GameId[]
  questions: number
}

/**
 * Whether this child's answers are being saved NOW, and which layer decided —
 * from the child's current consent, the parent's opt-out and the school's
 * setting, so it is answered before any session exists. `recorded: true` comes
 * with `reason: 'DEFAULT'`. The server always sends a reason today; `null` is
 * reserved and means "unknown".
 */
export interface RecordingStatus {
  recorded: boolean
  reason: RecordingSource | null
}

export interface ChildLearning {
  playerId: string
  from: string
  to: string
  /** Always all five games, zero-filled. */
  areas: AreaSummary[]
  activity: ActivityDay[]
  recentWrong: AnswerRow[]
  recording: RecordingStatus
}

/**
 * `answered` = correct + wrong answers (every try); `wrong` = wrong answers
 * only, never a missed pose or an unanswered gate; `firstTryRate` = first-try
 * correct / `answered`, 0–1 to 3 decimals.
 */
export interface ItemDifficulty {
  game: GameId
  level: number
  itemId: string
  answered: number
  wrong: number
  firstTryRate: number
}

/** A child (class view), a class (school view) or a school (platform view). `children` is the roll — 1 for a child. */
export interface GroupRow {
  id: string
  name: string
  children: number
  questions: number
  correct: number
  /** First attempts only, like every time figure here. */
  avgTtrMs: number | null
  /** From the summed latency buckets; first attempts only. */
  medianTtrMs: number | null
}

export interface GroupLearning {
  scope: { kind: 'class' | 'school' | 'platform'; id: string | null }
  from: string
  to: string
  children: { active: number; total: number }
  areas: AreaSummary[]
  mostMissed: ItemDifficulty[]
  breakdown: Page<GroupRow>
}

/* ── staff test play — a staff member's own "Try games" answers, never a child's ── */

/** The most records one `POST /learning/test-plays` may carry. */
export const TEST_PLAY_BATCH_MAX = 100

export interface TestPlayRow {
  id: string
  at: string
  runId: string
  game: GameId
  level: 1 | 2 | 3
  itemId: string
  outcome: QuestionOutcome
  attempt: number
  given: string | null
  expected: string | null
  ttrMs: number | null
  ttfMs: number | null
  movement: AnswerMovement
  moved: boolean
  gesture: string | null
  userId: string
  userName: string | null
}

/** ≤ TEST_PLAY_BATCH_MAX records; `clientId` is a v4 UUID per row (idempotent retries), `at` an ISO time. */
export interface TestPlayBatch {
  runId: string
  game: GameId
  level: 1 | 2 | 3
  records: (QuestionRecord & { clientId: string; at: string })[]
}

/* ── fetchers ────────────────────────────────────────────────────────────── */

export interface Auth {
  token: string
  signal?: AbortSignal
  /** `user` for every console; `device` for the child's own view on a kid device. */
  context?: Extract<AuthContext, 'user' | 'device'>
}

/** `YYYY-MM-DD` bounds, inclusive, IST — both or neither. */
export interface Range {
  from?: string
  to?: string
}

export interface Paging {
  cursor?: string | null
  /** Clamped to 1..100 here as well as on the server. */
  limit?: number
}

const PREFIX = '/learning'

/** `?a=1&b=x`, skipping anything undefined, null or empty. */
function query(params: Record<string, string | number | null | undefined>): string {
  const q = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    q.set(key, String(value))
  }
  const s = q.toString()
  return s ? `?${s}` : ''
}

const clampLimit = (limit: number | undefined): number | undefined =>
  limit === undefined ? undefined : Math.min(Math.max(Math.trunc(limit) || 1, 1), PAGE_LIMIT_MAX)

const get = <T>(path: string, auth: Auth): Promise<T> =>
  request<T>(path, { context: auth.context ?? 'user', token: auth.token, signal: auth.signal })

const id = (value: string): string => encodeURIComponent(value)

export interface AnswerFilter extends Range, Paging {
  game?: GameId
  level?: 1 | 2 | 3
  outcome?: QuestionOutcome
}

/** `GET /learning/players/:playerId/answers` — one child's answers, newest first. */
export const getPlayerAnswers = (playerId: string, filter: AnswerFilter, auth: Auth): Promise<Page<AnswerRow>> =>
  get(
    `${PREFIX}/players/${id(playerId)}/answers${query({
      game: filter.game,
      level: filter.level,
      outcome: filter.outcome,
      from: filter.from,
      to: filter.to,
      cursor: filter.cursor,
      limit: clampLimit(filter.limit),
    })}`,
    auth,
  )

/** `GET /learning/players/:playerId/summary` — the five areas, activity, recent wrong answers, recording state. */
export const getPlayerSummary = (playerId: string, range: Range, auth: Auth): Promise<ChildLearning> =>
  get(`${PREFIX}/players/${id(playerId)}/summary${query({ from: range.from, to: range.to })}`, auth)

type GroupQuery = Range & Pick<Paging, 'cursor'>

/** `GET /learning/classes/:classId` — a class, with its children as the breakdown. */
export const getClassLearning = (classId: string, q: GroupQuery, auth: Auth): Promise<GroupLearning> =>
  get(`${PREFIX}/classes/${id(classId)}${query({ from: q.from, to: q.to, cursor: q.cursor })}`, auth)

/** `GET /learning/schools/:schoolId` — a school, with its classes as the breakdown. */
export const getSchoolLearning = (schoolId: string, q: GroupQuery, auth: Auth): Promise<GroupLearning> =>
  get(`${PREFIX}/schools/${id(schoolId)}${query({ from: q.from, to: q.to, cursor: q.cursor })}`, auth)

/** `GET /learning/schools/me` — the caller's own school (school ADMIN / PRINCIPAL; the school comes from the token). */
export const getMySchoolLearning = (q: GroupQuery, auth: Auth): Promise<GroupLearning> =>
  get(`${PREFIX}/schools/me${query({ from: q.from, to: q.to, cursor: q.cursor })}`, auth)

/** `GET /learning/platform` — Kalqy staff only; schools as the breakdown. */
export const getPlatformLearning = (q: GroupQuery, auth: Auth): Promise<GroupLearning> =>
  get(`${PREFIX}/platform${query({ from: q.from, to: q.to, cursor: q.cursor })}`, auth)

/* ── breakdown pages alone — page 2+ without recomputing areas and most-missed ── */

type BreakdownQuery = Range & Paging

const breakdownQuery = (q: BreakdownQuery): string =>
  query({ from: q.from, to: q.to, cursor: q.cursor, limit: clampLimit(q.limit) })

/** `GET /learning/classes/:classId/breakdown` — the next page of a class's children, nothing else. */
export const getClassBreakdown = (classId: string, q: BreakdownQuery, auth: Auth): Promise<Page<GroupRow>> =>
  get(`${PREFIX}/classes/${id(classId)}/breakdown${breakdownQuery(q)}`, auth)

/** `GET /learning/schools/:schoolId/breakdown` — the next page of a school's classes. */
export const getSchoolBreakdown = (schoolId: string, q: BreakdownQuery, auth: Auth): Promise<Page<GroupRow>> =>
  get(`${PREFIX}/schools/${id(schoolId)}/breakdown${breakdownQuery(q)}`, auth)

/** `GET /learning/platform/breakdown` — the next page of schools (Kalqy staff only). */
export const getPlatformBreakdown = (q: BreakdownQuery, auth: Auth): Promise<Page<GroupRow>> =>
  get(`${PREFIX}/platform/breakdown${breakdownQuery(q)}`, auth)

type ItemQuery = Range & Paging & { game?: GameId; level?: 1 | 2 | 3 }

const itemQuery = (q: ItemQuery): string =>
  query({ from: q.from, to: q.to, cursor: q.cursor, limit: clampLimit(q.limit), game: q.game, level: q.level })

/** `GET /learning/classes/:classId/items` — the class's most-missed items. */
export const getClassItems = (classId: string, q: ItemQuery, auth: Auth): Promise<Page<ItemDifficulty>> =>
  get(`${PREFIX}/classes/${id(classId)}/items${itemQuery(q)}`, auth)

/** `GET /learning/schools/:schoolId/items`. */
export const getSchoolItems = (schoolId: string, q: ItemQuery, auth: Auth): Promise<Page<ItemDifficulty>> =>
  get(`${PREFIX}/schools/${id(schoolId)}/items${itemQuery(q)}`, auth)

/** `GET /learning/platform/items` — Kalqy staff only. */
export const getPlatformItems = (q: ItemQuery, auth: Auth): Promise<Page<ItemDifficulty>> =>
  get(`${PREFIX}/platform/items${itemQuery(q)}`, auth)

/**
 * `POST /learning/test-plays` — a staff member's own test-play answers. Never a
 * child's data: the server writes `test_play_attempt` only, with no player,
 * session or event. Retrying a batch is safe (per-row `clientId`).
 */
export const saveTestPlays = (batch: TestPlayBatch, auth: Auth): Promise<{ saved: number }> =>
  request<{ saved: number }>(`${PREFIX}/test-plays`, {
    method: 'POST',
    body: batch,
    context: 'user',
    token: auth.token,
    signal: auth.signal,
  })

export interface TestPlayFilter extends Pick<Paging, 'cursor' | 'limit'> {
  /** `mine` — the caller's own runs; `school` — every staff member's at the caller's school. */
  scope: 'mine' | 'school'
  game?: GameId
}

/** `GET /learning/test-plays` — test-play answers, newest first. */
export const getTestPlays = (filter: TestPlayFilter, auth: Auth): Promise<Page<TestPlayRow>> =>
  get(
    `${PREFIX}/test-plays${query({ scope: filter.scope, game: filter.game, cursor: filter.cursor, limit: clampLimit(filter.limit) })}`,
    { ...auth, context: 'user' },
  )
